/* eslint-disable semantic/ask -- o checkout (curseduca-front) não tem mecanismo de tradução: todo texto da tela é pt-BR escrito no componente, como no resto do app. */
import { Clock } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { QueryClient, useQuery } from 'react-query';

import { apiFactory } from '../../../api/ApiFactory';
import { applicationsApi } from '../../../api/applications';
import { trackPaymentsUnavailableView } from '../components/PageBlocks/behavior-tracker';
import { FinanceParams } from '../../../api/applications/types/applications.types';

/** De quanto em quanto tempo a tela confere sozinha se os pagamentos voltaram. */
export const PAYMENTS_UNAVAILABLE_REFETCH_MS = 60_000;

/**
 * A cobrança voltou 409 `seller_not_active`: o status mudou com a página já aberta. Marca o cache do
 * `/applications/params` como indisponível — o `App` passa isso ao contexto e a página troca o
 * formulário por este estado, igual a quem já chega com o params dizendo `false`. Uma fonte só: a
 * volta (o "Tentar novamente" ou a conferência de 60 s) é a mesma leitura do params.
 */
export const markPaymentsUnavailable = (queryClient: QueryClient) =>
	queryClient.setQueryData<FinanceParams>(
		[applicationsApi.getFinanceParams.key],
		// Sem params carregado (falhou e o checkout seguiu aberto): o objeto
		// parcial basta — o `App` completa `isCurseducaPayConfigured` e o
		// formulário nem aparece mais.
		(current) => ({
			...(current as FinanceParams),
			paymentsAvailable: false
		})
	);

/**
 * A plataforma não pode receber agora (`paymentsAvailable: false` do `/applications/params`, ou o
 * 409 `seller_not_active` de uma cobrança). No lugar do formulário: o comprador não preenche nada
 * para descobrir no fim que não dá.
 *
 * O texto é neutro de propósito — nada do vendedor, do cadastro nem do motivo. "Vendedor em análise
 * ou pausado" lido pelo comprador soa como vendedor suspeito; o que ele precisa saber é que agora
 * não dá e que pode tentar daqui a pouco.
 *
 * "Tentar novamente" e a conferência a cada 60 s refazem o `/applications/params`: quando ele volta
 * com o pagamento liberado, o `App` atualiza o contexto e a página volta para o formulário sozinha.
 *
 * Com `checkoutId`, avisa o pay (`payments_unavailable_view` no beacon de eventos do checkout) uma
 * vez por carga — é o que o alerta conta. Sem checkout (tela de planos, produto sem checkout
 * customizado) não há onde gravar e nada é enviado.
 */
export const PaymentsUnavailable = ({
	checkoutId
}: {
	checkoutId?: number;
}) => {
	useEffect(() => {
		if (!checkoutId) return;
		apiFactory
			.getInstance()
			.getApiKey()
			.then((apiKey) =>
				trackPaymentsUnavailableView({
					checkoutId,
					payUrl: process.env.REACT_APP_CURSEDUCA_PAY_DOMAIN || '',
					apiKey
				})
			)
			// Telemetria: sem api key não há o que mandar, e o estado na
			// tela não depende disto.
			.catch(() => undefined);
	}, [checkoutId]);

	// Mesmo cache do `App`: o refetch daqui atualiza o contexto da aplicação.
	// `refetchOnMount: false` — ao entrar neste estado por um 409 o cache foi
	// marcado à mão; refazer a leitura logo na montagem poderia devolver o
	// formulário e o comprador bater no mesmo 409.
	const { refetch, isFetching } = useQuery(
		[applicationsApi.getFinanceParams.key],
		applicationsApi.getFinanceParams.fetch,
		{
			refetchOnMount: false,
			refetchOnWindowFocus: false,
			refetchInterval: PAYMENTS_UNAVAILABLE_REFETCH_MS
		}
	);

	return (
		<div
			className="container d-flex align-items-center justify-content-center"
			style={{ minHeight: '100vh', maxWidth: 600, padding: 16 }}
		>
			<div className="card card-custom my-auto w-100" role="status">
				<div className="card-body">
					<div className="text-center">
						<Clock
							size={48}
							className="text-muted mb-4"
							aria-hidden="true"
						/>
						<h1 className="font-size-h3 text-dark-75 font-weight-bold">
							Pagamentos temporariamente indisponíveis
						</h1>
						<p className="text-muted font-size-lg">
							Não conseguimos processar pagamentos nesta página
							agora. Tente novamente em alguns minutos.
						</p>
						<button
							type="button"
							className="btn btn-primary mt-2"
							disabled={isFetching}
							onClick={() => void refetch()}
						>
							{isFetching ? 'Verificando…' : 'Tentar novamente'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
